import { MediaImage } from "@/components/admin/MediaAsset";
import { useEffect, useState } from "react";
import { CalendarPlus, Loader2, Plus, X, Save, LayoutTemplate, Trash2, Star, Users2, ImageIcon, Repeat, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MediaGallery from "@/components/admin/MediaGallery";

const CHANNELS = ["instagram", "facebook", "tiktok", "blog", "umum"];
const REMINDERS = [
  { v: "0", l: "Tanpa pengingat" },
  { v: "5", l: "5 menit sebelum" },
  { v: "10", l: "10 menit sebelum" },
  { v: "15", l: "15 menit sebelum" },
  { v: "30", l: "30 menit sebelum" },
  { v: "60", l: "1 jam sebelum" },
];
const REPEATS = [
  { v: "none", l: "Sekali saja" },
  { v: "daily", l: "Harian" },
  { v: "weekly", l: "Mingguan" },
];

function dateInput(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function NewScheduleDialog({ open, onClose, presetDate, businesses = [], onCreated }) {
  const [bizId, setBizId] = useState(businesses[0]?.id || "");
  const [channel, setChannel] = useState("instagram");
  const [date, setDate] = useState(presetDate ? dateInput(presetDate) : dateInput(new Date()));
  const [time, setTime] = useState("09:00");
  const [text, setText] = useState("");
  const [reminder, setReminder] = useState("15");
  const [repeat, setRepeat] = useState("none");
  const [endDate, setEndDate] = useState("");
  const [targets, setTargets] = useState([]);
  const [targetInput, setTargetInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [autoSocial, setAutoSocial] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [teamNumbers, setTeamNumbers] = useState([]);

  const loadTemplates = () => api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { if (open) { loadTemplates(); api.get("/team-numbers").then((r) => setTeamNumbers(r.data || [])).catch(() => {}); } }, [open]);

  const addNumber = (num) => { if (num && !targets.includes(num)) setTargets((t) => [...t, num]); };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setText(t.text);
    if (t.channel) setChannel(t.channel);
    api.post(`/templates/${t.id}/use`).catch(() => {});
    toast.success(`Template "${t.title}" diterapkan`);
  };

  const saveTemplate = async () => {
    if (!text.trim()) { toast.error("Isi konten dulu sebelum menyimpan template"); return; }
    const title = window.prompt("Nama template:");
    if (!title || !title.trim()) return;
    try {
      await api.post("/templates", { title: title.trim(), channel: channel === "umum" ? "" : channel, text });
      toast.success("Template disimpan");
      loadTemplates();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/templates/${id}`);
    toast.success("Template dihapus");
    loadTemplates();
  };

  const addTarget = () => {
    const digits = targetInput.replace(/[^0-9]/g, "");
    if (!digits) { toast.error("Nomor tidak boleh kosong"); return; }
    if (digits.length < 8 || digits.length > 15) { toast.error("Nomor WhatsApp tidak valid (8–15 digit)"); return; }
    const norm = digits.replace(/^0/, "62");
    if (targets.includes(norm)) { toast.error("Nomor sudah ditambahkan"); setTargetInput(""); return; }
    setTargets([...targets, norm]);
    setTargetInput("");
  };

  const save = async () => {
    if (!bizId) { toast.error("Pilih bisnis"); return; }
    if (!text.trim()) { toast.error("Isi konten tidak boleh kosong"); return; }
    if (!date || !time) { toast.error("Pilih tanggal & waktu"); return; }
    if (repeat !== "none" && !endDate) { toast.error("Isi tanggal berakhir untuk jadwal berulang"); return; }
    if (autoSocial.includes("instagram") && !imageUrls.length) { toast.error("Instagram wajib menyertakan gambar"); return; }
    setBusy(true);
    try {
      const sendAt = new Date(`${date}T${time}`).toISOString();
      const r = await api.post("/scheduled-posts", {
        business_id: bizId,
        text,
        channel: channel === "umum" ? "" : channel,
        targets,
        send_at: sendAt,
        reminder_minutes: parseInt(reminder, 10),
        image_url: imageUrls[0] || "",
        image_urls: imageUrls,
        repeat,
        repeat_until: repeat === "none" ? "" : endDate,
        auto_social: autoSocial,
      });
      toast.success(r.data?.created ? `${r.data.created} jadwal ${repeat === "daily" ? "harian" : "mingguan"} dibuat` : "Jadwal dibuat");
      onCreated && onCreated();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto" data-testid="new-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarPlus size={18} className="text-[#2563EB]" /> Jadwal Posting Baru</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Jadwalkan kiriman WhatsApp atau auto-post Facebook/Instagram.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Bisnis</Label>
            <Select value={bizId} onValueChange={setBizId}>
              <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="new-schedule-business"><SelectValue placeholder="Pilih bisnis" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 capitalize" data-testid="new-schedule-channel"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  {CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Pengingat</Label>
              <Select value={reminder} onValueChange={setReminder}>
                <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="new-schedule-reminder"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  {REMINDERS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Tanggal</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="new-schedule-date" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Waktu</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="new-schedule-time" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><Repeat size={12} /> Ulangi</Label>
              <Select value={repeat} onValueChange={setRepeat}>
                <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="new-schedule-repeat"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  {REPEATS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Berakhir pada</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={repeat === "none"} min={date} data-testid="new-schedule-repeat-until" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 disabled:opacity-40" />
            </div>
          </div>
          {repeat !== "none" && (
            <p className="text-[11px] text-slate-500 -mt-1" data-testid="new-schedule-repeat-hint">
              Jadwal dibuat otomatis setiap {repeat === "daily" ? "hari" : "minggu"} pada jam yang sama sampai tanggal berakhir (maks. 90 kiriman).
            </p>
          )}

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-600 flex items-center gap-1"><LayoutTemplate size={13} /> Template Konten</Label>
              <button onClick={saveTemplate} type="button" data-testid="save-template-button" className="text-xs text-[#2563EB] hover:underline flex items-center gap-1"><Save size={12} /> Simpan sebagai template</button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" data-testid="template-select" className="w-full justify-between border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl mt-1 font-normal text-slate-800">
                  {templates.length ? "Pilih template untuk mengisi konten" : "Belum ada template"}
                  <LayoutTemplate size={14} className="text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 w-[var(--radix-dropdown-menu-trigger-width)]">
                {templates.length === 0 && <div className="px-2 py-2 text-xs text-slate-500">Belum ada template tersimpan</div>}
                {[...templates].sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0) || (b.usage_count || 0) - (a.usage_count || 0)).map((t) => (
                  <DropdownMenuItem key={t.id} onSelect={() => applyTemplate(t.id)} data-testid={`template-item-${t.id}`} className="flex items-center justify-between gap-2 cursor-pointer focus:bg-[#F1F5F9]">
                    <span className="text-sm truncate flex items-center gap-1.5">
                      <span className="truncate">{t.title}{t.channel ? ` · ${t.channel}` : ""}</span>
                      {(t.rating_avg || 0) > 0 && (
                        <span className="flex items-center gap-0.5 text-[11px] text-slate-600 shrink-0" data-testid={`template-item-score-${t.id}`}>
                          <Star size={11} className="text-[#D97706] fill-[#D97706]" />{t.rating_avg}
                        </span>
                      )}
                    </span>
                    <button onClick={(e) => deleteTemplate(t.id, e)} className="text-slate-500 hover:text-[#E11D48] shrink-0"><Trash2 size={13} /></button>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Isi Konten</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="new-schedule-text" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Tulis konten yang akan dikirim..." />
          </div>

          <div>
            <Label className="text-xs text-slate-600">Nomor tujuan (kosong = nomor admin bisnis)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={targetInput} onChange={(e) => setTargetInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTarget(); } }} data-testid="new-schedule-target-input" className="bg-[#F1F5F9] border-[#E2E8F0]" placeholder="628xxxxxxxxxx lalu Enter" />
              <Button type="button" onClick={addTarget} variant="outline" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl shrink-0"><Plus size={16} /></Button>
            </div>
            {teamNumbers.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="new-schedule-team-numbers">
                <span className="text-[11px] text-slate-500 flex items-center gap-1"><Users2 size={11} /> Tim:</span>
                {teamNumbers.map((n) => (
                  <button key={n.number} type="button" onClick={() => addNumber(n.number)} data-testid={`new-schedule-team-pick-${n.number}`} className="text-xs bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2.5 py-1 text-slate-800 hover:border-[#3B82F6] hover:text-slate-900 transition-colors">
                    + {n.label}
                  </button>
                ))}
              </div>
            )}
            {targets.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {targets.map((t) => (
                  <span key={t} className="flex items-center gap-1 bg-[#E2E8F0] border border-[#E2E8F0] rounded-full pl-3 pr-1 py-1 text-xs">
                    {t}<button onClick={() => setTargets(targets.filter((x) => x !== t))} className="w-4 h-4 rounded-full hover:bg-[#E11D48]/20 flex items-center justify-center"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-600 flex items-center gap-1"><ImageIcon size={12} /> Gambar (opsional, bisa banyak untuk carousel)</Label>
              <button type="button" onClick={() => setGalleryOpen(true)} data-testid="new-schedule-attach-image" className="text-xs text-[#2563EB] hover:underline">Lampirkan dari Galeri</button>
            </div>
            {imageUrls.length > 0 && (
              <div className="mt-2" data-testid="new-schedule-image-preview">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {imageUrls.map((url, i) => (
                    <div
                      key={url}
                      draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragIdx === null || dragIdx === i) return;
                        const arr = [...imageUrls];
                        const [moved] = arr.splice(dragIdx, 1);
                        arr.splice(i, 0, moved);
                        setImageUrls(arr);
                        setDragIdx(null);
                      }}
                      data-testid={`new-schedule-image-${i}`}
                      className={`relative shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-opacity ${dragIdx === i ? "opacity-40 border-[#7C3AED]" : "border-[#3B82F6]"}`}
                    >
                      <MediaImage src={url} alt={`gambar ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                      <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[#3B82F6] text-white text-[9px] font-bold flex items-center justify-center">{i + 1}</span>
                      <button type="button" onClick={() => setImageUrls(imageUrls.filter((x) => x !== url))} data-testid={`new-schedule-image-remove-${i}`} className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#F8FAFC]/80 text-slate-800 hover:text-[#E11D48] flex items-center justify-center"><X size={9} /></button>
                    </div>
                  ))}
                </div>
                {imageUrls.length > 1 && <p className="text-[10px] text-slate-500 mt-1" data-testid="new-schedule-reorder-hint">Seret gambar untuk mengubah urutan slide carousel.</p>}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="new-schedule-autopilot">
            <div className="flex items-center gap-2 mb-1">
              <Rocket size={14} className="text-[#7C3AED]" />
              <Label className="text-xs text-slate-800 font-medium">Autopilot — posting otomatis ke sosial media</Label>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">Saat jadwal jatuh tempo, konten langsung diposting ke platform terpilih via Meta (banyak gambar = carousel otomatis).</p>
            <div className="flex gap-2">
              {[{ v: "facebook", l: "Facebook" }, { v: "instagram", l: "Instagram" }].map((o) => {
                const on = autoSocial.includes(o.v);
                return (
                  <button key={o.v} type="button" data-testid={`new-schedule-autopilot-${o.v}`}
                    onClick={() => setAutoSocial(on ? autoSocial.filter((x) => x !== o.v) : [...autoSocial, o.v])}
                    className={`flex-1 text-xs rounded-lg px-3 py-2 border transition-colors ${on ? "border-[#7C3AED] bg-[#7C3AED]/15 text-[#7C3AED] font-semibold" : "border-[#E2E8F0] bg-[#F1F5F9] text-slate-600 hover:border-[#334155]"}`}>
                    {on ? "✓ " : ""}{o.l}
                  </button>
                );
              })}
            </div>
            {autoSocial.includes("instagram") && imageUrls.length === 0 && (
              <p className="text-[11px] text-[#D97706] mt-2" data-testid="new-schedule-autopilot-ig-warn">Instagram butuh gambar — lampirkan dari galeri di atas.</p>
            )}
          </div>

          <Button onClick={save} disabled={busy} data-testid="new-schedule-save" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
            {busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <CalendarPlus size={16} className="mr-1" />} Buat Jadwal
          </Button>
        </div>
      </DialogContent>

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-2xl max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Pilih Gambar</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Pilih gambar dari galeri media untuk dilampirkan ke jadwal.</DialogDescription>
          </DialogHeader>
          <MediaGallery picker multi onPick={(urls) => { setImageUrls(urls); setGalleryOpen(false); toast.success(`${urls.length} gambar dilampirkan`); }} />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
